import { useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import { GlobalFilterProvider } from '@panwds/react-filters'
import Main from './Main'
import { store, getState, dispatch, stateRef } from '../../framework/store'
import { GlobalStateContext, useStoreReducer } from '../common/storeHook'
import reducer from '../common/reducer'
import events from '../../framework/events'
import { dynamicMicroAppsTsgLoad, resolveMicroApps } from '../common/routes'
import { ErrorBoundary } from '../common/ErrorBoundary'
import { isLoginFailed } from './fawkesUtils'
import { clearLoadingMask } from '../../framework/loadingMask'
import { OidcAuthProvider } from '../auth/OidcAuth'
import { authenticated, loaded } from '../../framework/states'
import { SPARKY_FRAMEWORK } from '../../framework/constants'
import { isInSilentLoginIframe, tryParseJson } from '../../init'
import { forceRender, useRerender } from '../common/routeRender'
import useLogout from '../auth/useLogout'
import useTsgErrorModal from '../auth/useTsgErrorModal'
import {
  useFawkesSession,
  useThemePreference,
  useTsgTenantInfo,
  useTsgTitleSync,
  useVarConfigEffect,
} from '../common/appEffects'
import { cache } from '../../framework/cache'
import { __info } from '../../init/debug'
import type { VFC, ReactElement } from 'react'
import type { match as Match } from 'react-router'
import type { Location } from 'history'
import type { AuthState, FrameworkVars, MainState, StoreState } from '../../framework/stateTypes'
import type { SparkySettings } from '../../framework/microAppTypes'

const DEFAULT_AUTH = { loading: true } as AuthState
const DEFAULT_LOCALE = 'en-US'

const AppContainer: VFC<{ settings: SparkySettings, match?: Match, location?: Location }> = ({ settings, match, location }): ReactElement => {
  const state = useStoreReducer(reducer, settings, (settings) => {
    const vars = (settings.variables[SPARKY_FRAMEWORK] || {}) as FrameworkVars
    const classicAppSwitcherVisible = settings.lite ? Boolean(vars.show_app_switcher) : !(vars.msp_tenant_switcher || vars.sase || vars.hub || vars.private_site_origin) && vars.show_app_switcher !== false
    const initState = {
      match,
      location,
      locationRoute: null,
      menuItemPath: null,
      activeItem: null,
      forceLogin: false,
      isSideNavCollapsed: false,
      cache,
      vars: settings.variables,
      exposes: settings.exposes,
      microAppNameSet: settings.microAppNameSet,
      leftMainNavigation: Object.assign([], { flattenNavigationList: [], pathAvailMap: {}, initializing: true }),
      leftMainActions: [],
      navRoutes: Object.defineProperties([], { loading: { value: true } }),
      loginFailed: isLoginFailed(),
      ipRestrict: window.location.search.includes('ipRestrict=true'),
      menuData: [],
      auth: DEFAULT_AUTH,
      main: { configLocation: {} } as MainState,
      classicAppSwitcher: classicAppSwitcherVisible ? {
        visible: classicAppSwitcherVisible,
        loading: true,
        allowLinkCallBackApps: settings.lite ? [] : ['*'],
      } : { visible: false },
      preferences: {
        user: tryParseJson(sessionStorage.spk_user_pref) || {
          theme: localStorage.spk_user_theme || vars.default_user_theme
        }
      },
    }
    if (!isInSilentLoginIframe) {
      const microAppInit = async () => {
        await authenticated
        const initedMicroAppsPromise = resolveMicroApps(settings.navigation)
        await loaded
        return dynamicMicroAppsTsgLoad(stateRef.current, await initedMicroAppsPromise)
      }
      Object.assign(initState, {
        microAppsInitPromise: microAppInit()
      })
    }
    else {
      __info('silent sign-in callback in iframe, skip init micro-apps')
    }
    return initState
  }) as StoreState
  useEffect(() => {
    if (state.microAppsInitPromise) {
      state.microAppsInitPromise.then(forceRender, (err: Error) => {
        if (err.message === 'redirection') {
          return
        }
        clearLoadingMask()
        console.error('failed to init micro-apps', err)
      })
    }
  }, [state.microAppsInitPromise, settings.exposes])

  useTsgTitleSync(state.auth)
  useTsgTenantInfo(state.auth)
  useLogout(state)
  useRerender()
  useVarConfigEffect()
  useThemePreference(state.themeOverride || state.preferences?.user?.theme)
  useFawkesSession(state.auth)

  return (
    <ErrorBoundary context='app-main'>
      <GlobalStateContext.Provider value={store}>
        <IntlProvider defaultLocale={DEFAULT_LOCALE} locale={navigator.language}>
          <OidcAuthProvider settings={settings}>
            <GlobalFilterProvider>
              <Main
                state={state}
                main={state.main}
                oidc={state.oidc}
                getState={getState}
                stateRef={stateRef}
                dispatch={dispatch}
                events={events}
              />
            </GlobalFilterProvider>
          </OidcAuthProvider>
          {useTsgErrorModal()}
        </IntlProvider>
      </GlobalStateContext.Provider>
    </ErrorBoundary>
  )
}

export default AppContainer
